/**
 * GraphQL Share Query Hooks
 *
 * Hooks for share-related queries: search recipients, get share info, etc.
 *
 * Feature: 080-critique-sharing
 * Tasks: T053
 */

import { useLazyQuery, gql } from '@apollo/client';
import { useCallback } from 'react';
import logger from '../../utils/logger';

// ============================================================================
// GraphQL Queries
// ============================================================================

/**
 * Query to search for share recipients by username, display name, or email
 */
const SEARCH_SHARE_RECIPIENTS_QUERY = gql`
  query SearchShareRecipients($query: String!, $limit: Int) {
    searchShareRecipients(query: $query, limit: $limit) {
      type
      userId
      username
      displayName
      avatarUrl
      email
      isPending
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type ShareRecipientType = 'USER' | 'EMAIL';

export interface ShareRecipient {
  type: ShareRecipientType;
  userId: string | null;
  username: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  email: string | null;
  isPending: boolean;
}

interface SearchShareRecipientsData {
  searchShareRecipients: ShareRecipient[];
}

interface SearchShareRecipientsVariables {
  query: string;
  limit?: number;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for searching share recipients
 *
 * Searches for users by username, display name, or email.
 * Returns matching users and, if the query looks like an email,
 * offers to share with that email directly.
 */
export function useSearchShareRecipientsGraphQL() {
  const [searchQuery, { data, loading, error }] = useLazyQuery<
    SearchShareRecipientsData,
    SearchShareRecipientsVariables
  >(SEARCH_SHARE_RECIPIENTS_QUERY);

  const searchRecipients = useCallback(
    async (query: string, limit: number = 10): Promise<ShareRecipient[]> => {
      // Don't search if query is too short
      if (query.trim().length < 2) {
        return [];
      }

      logger.debug('Sharing', 'Searching for recipients', { query, limit });

      try {
        const result = await searchQuery({
          variables: { query: query.trim(), limit },
          fetchPolicy: 'network-only', // Always fetch fresh results
        });

        const recipients = result.data?.searchShareRecipients || [];

        logger.debug('Sharing', 'Found recipients', {
          query,
          count: recipients.length,
        });

        return recipients;
      } catch (err) {
        logger.error('Sharing', 'Failed to search recipients', { query, error: err });
        throw err;
      }
    },
    [searchQuery]
  );

  return {
    searchRecipients,
    data: data?.searchShareRecipients || [],
    loading,
    error,
  };
}

/**
 * Combined hook for all share queries
 */
export function useShareQueriesGraphQL() {
  return {
    useSearchShareRecipients: useSearchShareRecipientsGraphQL,
  };
}
